import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  isZipExportUnavailable,
  useDocumentZipExport,
} from '@/hooks/documents/useDocumentZipExport';

interface ZipExportNoticeProps {
  exportId: string;
  className?: string;
}

const skippedNote = (count: number) =>
  count > 0
    ? ` ${count} selected ${count === 1 ? 'file was' : 'files were'} not available and ${count === 1 ? 'is' : 'are'} not included.`
    : '';

/**
 * OV-1405. Shown in place of the old silent "Preparing…" wait when a download
 * is large enough to be built in the background. Polls the export and starts
 * the download by itself once the archive is ready.
 */
const ZipExportNotice = ({ exportId, className }: ZipExportNoticeProps) => {
  const { zipExport, error, download } = useDocumentZipExport(exportId, {
    autoDownload: true,
  });

  if (error || isZipExportUnavailable(zipExport)) {
    return (
      <Alert variant="destructive" className={className}>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>
          {zipExport?.status === 'expired'
            ? 'This download has expired'
            : 'We couldn’t prepare that download'}
        </AlertTitle>
        <AlertDescription>
          {zipExport?.failure_reason === 'not_servable'
            ? 'Some of those documents are no longer available. Refresh and try again.'
            : 'Please select the documents and download them again.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (zipExport?.download_url) {
    return (
      <Alert className={className}>
        <CheckCircle2 className="h-4 w-4 text-success-600" />
        <AlertTitle>Your download has started</AlertTitle>
        <AlertDescription>
          <span>
            {zipExport.filename} is ready.{skippedNote(zipExport.skipped_count)}{' '}
            <button
              type="button"
              className="font-medium text-link underline underline-offset-2"
              onClick={download}
            >
              Download again
            </button>
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className={className}>
      <Loader2 className="h-4 w-4 animate-spin" />
      <AlertTitle>Preparing your download…</AlertTitle>
      <AlertDescription>
        Large selections can take a few minutes. It will download automatically
        when it’s ready, and we’ll also email you a link, so you can leave this
        page.
      </AlertDescription>
    </Alert>
  );
};

export default ZipExportNotice;
